<template>
  <div class="lg-page">
    <div class="lg-grid-bg"></div>

    <nav class="lg-nav">
      <div class="lg-brand" @click="$router.push('/')">
        <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="14" fill="#d48c1c"/>
          <path d="M12 34V18l8 10 4-6 4 6 8-10v16" stroke="#0c0a0e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        </svg>
        <span class="lg-wordmark">Muzilly</span>
      </div>
      <div class="lg-links">
        <router-link to="/about" class="lg-link" :class="{ active: active === 'about' }">About</router-link>
        <router-link to="/faq" class="lg-link" :class="{ active: active === 'faq' }">FAQ</router-link>
        <router-link to="/privacy" class="lg-link" :class="{ active: active === 'privacy' }">Privacy</router-link>
        <router-link to="/terms" class="lg-link" :class="{ active: active === 'terms' }">Terms</router-link>
        <router-link to="/guidelines" class="lg-link" :class="{ active: active === 'guidelines' }">Guidelines</router-link>
        <button class="lg-back" @click="goBack">← Back</button>
      </div>
    </nav>

    <header class="lg-hero">
      <div class="lg-eyebrow">{{ eyebrow }}</div>
      <h1 class="lg-title">{{ title }}</h1>
      <p v-if="subtitle" class="lg-sub">{{ subtitle }}</p>
      <slot name="hero"></slot>
    </header>

    <div class="lg-layout" :class="{ 'no-toc': !sections.length }">
      <aside v-if="sections.length" class="lg-toc">
        <div class="lg-toc-label">On this page</div>
        <nav class="lg-toc-nav">
          <a v-for="s in sections" :key="s.id" :href="'#' + s.id" class="lg-toc-item"
             :class="{ active: activeSection === s.id }" @click.prevent="scrollTo(s.id)">
            <span class="lg-toc-dot"></span>{{ s.label }}
          </a>
        </nav>
        <div v-if="contactEmail" class="lg-toc-contact">
          <div class="lg-toc-clabel">{{ contactLabel }}</div>
          <a :href="'mailto:' + contactEmail" class="lg-toc-email">{{ contactEmail }}</a>
        </div>
      </aside>

      <main class="lg-body">
        <slot></slot>
        <div class="lg-footer">
          <div class="lg-footer-links">
            <router-link to="/about">About</router-link>
            <router-link to="/faq">FAQ</router-link>
            <router-link to="/privacy">Privacy</router-link>
            <router-link to="/terms">Terms</router-link>
            <router-link to="/delete-account">Delete account</router-link>
          </div>
          <div class="lg-footer-meta">© {{ year }} Muzilly · muzilly.com · support@muzilly.com</div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: "LegalShell",
  props: {
    active: { type: String, default: "" },
    eyebrow: { type: String, default: "" },
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    sections: { type: Array, default: () => [] },
    contactLabel: { type: String, default: "Questions?" },
    contactEmail: { type: String, default: "" },
  },
  data() {
    return { activeSection: this.sections.length ? this.sections[0].id : "", year: new Date().getFullYear() };
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    document.title = this.title + " · Muzilly";
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    goBack() {
      if (window.history.length > 2) this.$router.go(-1);
      else this.$router.push("/");
    },
    onScroll() {
      if (!this.sections.length) return;
      let best = null;
      let bestTop = Infinity;
      this.sections.forEach((s) => {
        const el = document.getElementById(s.id);
        if (!el) return;
        const d = Math.abs(el.getBoundingClientRect().top - 100);
        if (d < bestTop) { bestTop = d; best = s.id; }
      });
      if (best) this.activeSection = best;
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
};
</script>

<!-- Not scoped on purpose: the page components reuse the lg-* content classes below. -->
<style>
.lg-page{min-height:100vh;background:#0c0a0e;font-family:'DM Sans','Helvetica Neue',sans-serif;color:#f5f0e8;position:relative;overflow-x:hidden}
.lg-page *{box-sizing:border-box}
.lg-grid-bg{position:fixed;inset:0;pointer-events:none;z-index:0;background-image:linear-gradient(rgba(245,240,232,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(245,240,232,0.025) 1px,transparent 1px);background-size:60px 60px}

.lg-nav{position:sticky;top:0;z-index:100;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 40px;background:rgba(12,10,14,0.92);border-bottom:0.5px solid rgba(245,240,232,0.07);backdrop-filter:blur(12px)}
.lg-brand{display:flex;align-items:center;gap:10px;cursor:pointer}
.lg-wordmark{font-size:18px;font-weight:500;color:#f5f0e8;letter-spacing:-0.3px}
.lg-links{display:flex;align-items:center;gap:20px;flex-wrap:wrap;justify-content:flex-end}
.lg-link{font-size:13px;color:rgba(245,240,232,0.45);text-decoration:none;transition:color .18s}
.lg-link:hover{color:#f5f0e8}
.lg-link.active{color:#d48c1c}
.lg-back{background:none;border:0.5px solid rgba(245,240,232,0.1);border-radius:8px;padding:6px 14px;font-size:12px;color:rgba(245,240,232,0.45);font-family:inherit;cursor:pointer;transition:all .18s}
.lg-back:hover{border-color:rgba(245,240,232,0.25);color:#f5f0e8}

.lg-hero{position:relative;z-index:1;max-width:760px;margin:0 auto;padding:72px 40px 48px}
.lg-eyebrow{font-size:12px;color:rgba(245,240,232,0.35);letter-spacing:1px;text-transform:uppercase;margin-bottom:16px}
.lg-title{font-size:48px;font-weight:500;color:#f5f0e8;letter-spacing:-1px;line-height:1.1;margin:0 0 20px}
.lg-sub{font-size:16px;color:rgba(245,240,232,0.55);line-height:1.7;margin:0 0 28px;max-width:640px}
.lg-note{display:flex;align-items:flex-start;gap:12px;background:rgba(212,140,28,0.07);border:0.5px solid rgba(212,140,28,0.25);border-radius:11px;padding:16px 18px;max-width:640px;font-size:13px;color:rgba(245,240,232,0.7);line-height:1.6}
.lg-note.teal{background:rgba(93,202,165,0.06);border-color:rgba(93,202,165,0.2)}
.lg-note strong{color:#f5f0e8}

.lg-layout{position:relative;z-index:1;display:grid;grid-template-columns:240px 1fr;max-width:1100px;margin:0 auto;padding:0 40px 80px;align-items:start}
.lg-layout.no-toc{grid-template-columns:1fr;max-width:760px}
.lg-toc{position:sticky;top:80px;padding-right:40px;padding-bottom:40px}
.lg-toc-label{font-size:11px;color:rgba(245,240,232,0.3);letter-spacing:0.8px;text-transform:uppercase;margin-bottom:14px}
.lg-toc-nav{display:flex;flex-direction:column;gap:2px}
.lg-toc-item{display:flex;align-items:center;gap:8px;padding:5px 0;font-size:12px;color:rgba(245,240,232,0.35);text-decoration:none;cursor:pointer;transition:color .18s;line-height:1.4}
.lg-toc-item:hover{color:rgba(245,240,232,0.7)}
.lg-toc-item.active{color:#f5f0e8}
.lg-toc-dot{width:4px;height:4px;border-radius:50%;background:rgba(245,240,232,0.2);flex-shrink:0}
.lg-toc-item.active .lg-toc-dot{background:#d48c1c}
.lg-toc-contact{margin-top:28px;padding-top:18px;border-top:0.5px solid rgba(245,240,232,0.07)}
.lg-toc-clabel{font-size:11px;color:rgba(245,240,232,0.3);margin-bottom:5px}
.lg-toc-email{font-size:12px;color:#d48c1c;text-decoration:none}
.lg-toc-email:hover{text-decoration:underline}

.lg-body{min-width:0}
.lg-section{padding:52px 0;border-bottom:0.5px solid rgba(245,240,232,0.06);scroll-margin-top:80px}
.lg-section:first-child{padding-top:8px}
.lg-num{font-size:11px;color:rgba(245,240,232,0.2);letter-spacing:2px;margin-bottom:10px}
.lg-h2{font-size:26px;font-weight:500;color:#f5f0e8;letter-spacing:-0.4px;margin:0 0 18px}
.lg-p{font-size:14px;color:rgba(245,240,232,0.6);line-height:1.75;margin:0 0 16px}
.lg-p:last-child{margin-bottom:0}
.lg-p strong{color:#f5f0e8}
.lg-a{color:#d48c1c;text-decoration:none}
.lg-a:hover{text-decoration:underline}
.lg-list{padding-left:20px;display:flex;flex-direction:column;gap:10px;margin:8px 0 16px}
.lg-list li{font-size:14px;color:rgba(245,240,232,0.6);line-height:1.65}
.lg-list li strong{color:#f5f0e8}
.lg-steps{counter-reset:s;list-style:none;padding:0;margin:8px 0 16px;display:flex;flex-direction:column;gap:10px}
.lg-steps li{counter-increment:s;display:flex;gap:14px;align-items:flex-start;font-size:14px;color:rgba(245,240,232,0.7);line-height:1.6;background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:11px;padding:14px 16px}
.lg-steps li::before{content:counter(s);width:24px;height:24px;border-radius:50%;background:rgba(212,140,28,0.14);color:#d48c1c;font-size:12px;font-weight:600;display:flex;align-items:center;justify-content:center;flex-shrink:0}

.lg-callout{background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.08);border-left:3px solid rgba(212,140,28,0.5);border-radius:0 11px 11px 0;padding:18px 20px;margin:20px 0}
.lg-callout.teal{border-left-color:rgba(93,202,165,0.5)}
.lg-callout.red{border-left-color:rgba(196,112,112,0.6)}
.lg-callout-title{font-size:13px;font-weight:500;color:#f5f0e8;margin-bottom:10px}
.lg-callout .lg-p,.lg-callout .lg-list li{font-size:13px}

.lg-items{display:flex;flex-direction:column;gap:10px;margin-top:8px}
.lg-item{display:flex;align-items:flex-start;gap:12px;background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:11px;padding:15px}
.lg-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0;margin-top:6px;background:#d48c1c}
.lg-dot.teal{background:#5dcaa5}
.lg-item-title{font-size:13px;font-weight:500;color:#f5f0e8;margin-bottom:5px}
.lg-item-text{font-size:13px;color:rgba(245,240,232,0.55);line-height:1.65}

.lg-cards{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:8px}
.lg-cards.three{grid-template-columns:1fr 1fr 1fr}
.lg-card{background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.07);border-radius:11px;padding:16px}
.lg-card-title{font-size:13px;font-weight:500;color:#f5f0e8;margin-bottom:8px}
.lg-card-text{font-size:13px;color:rgba(245,240,232,0.55);line-height:1.65}

.lg-table{border:0.5px solid rgba(245,240,232,0.08);border-radius:11px;overflow:hidden;margin:8px 0 20px}
.lg-tr{display:grid;gap:12px;padding:13px 14px;border-bottom:0.5px solid rgba(245,240,232,0.05);font-size:13px;color:rgba(245,240,232,0.55);line-height:1.5;align-items:start}
.lg-tr:last-child{border-bottom:none}
.lg-tr.head{background:rgba(245,240,232,0.04);font-size:11px;color:rgba(245,240,232,0.35);letter-spacing:0.4px}
.lg-tr.c3{grid-template-columns:1.1fr 2fr 1.4fr}
.lg-tr.c4{grid-template-columns:1.1fr 2fr 1.6fr 1.2fr}
.lg-tr b{color:#f5f0e8;font-weight:500}

.lg-footer{margin-top:56px;text-align:center;padding:24px 0 0}
.lg-footer-links{display:flex;justify-content:center;gap:18px;flex-wrap:wrap;margin-bottom:12px}
.lg-footer-links a{font-size:12px;color:rgba(245,240,232,0.45);text-decoration:none}
.lg-footer-links a:hover{color:#d48c1c}
.lg-footer-meta{font-size:11px;color:rgba(245,240,232,0.25)}

.lg-cta{margin-top:40px;text-align:center;background:rgba(245,240,232,0.03);border:0.5px solid rgba(245,240,232,0.08);border-radius:16px;padding:40px 28px}
.lg-cta-title{font-size:20px;font-weight:500;color:#f5f0e8;margin:0 0 10px}
.lg-cta-text{font-size:14px;color:rgba(245,240,232,0.5);margin:0 0 22px}
.lg-btn{display:inline-block;background:#d48c1c;border:none;border-radius:9px;padding:11px 24px;font-size:13px;font-weight:500;color:#0c0a0e;text-decoration:none;cursor:pointer;font-family:inherit;transition:opacity .18s}
.lg-btn:hover{opacity:.88}
.lg-btn:disabled{opacity:.4;cursor:default}
.lg-btn.danger{background:#b5413a;color:#fff}
.lg-btn.ghost{background:none;border:0.5px solid rgba(245,240,232,0.2);color:rgba(245,240,232,0.7)}

@media (max-width:900px){
  .lg-nav{padding:12px 16px}
  .lg-hero{padding:48px 24px 32px}
  .lg-title{font-size:34px}
  .lg-layout{grid-template-columns:1fr;padding:0 24px 60px}
  .lg-toc{display:none}
  .lg-cards,.lg-cards.three{grid-template-columns:1fr}
  .lg-tr.c3,.lg-tr.c4{grid-template-columns:1fr 1.6fr}
  .lg-tr.c3>:nth-child(3),.lg-tr.c4>:nth-child(3),.lg-tr.c4>:nth-child(4){display:none}
  .lg-tr.head.c3>:nth-child(3),.lg-tr.head.c4>:nth-child(3),.lg-tr.head.c4>:nth-child(4){display:none}
}
</style>